class UnderRigFunnel {
  constructor(root) {
    this.root = root;
    this.product = this.readJson('[data-ur-product-json]');
    this.moneyFormat = this.readJson('[data-ur-money-format]') || '${{amount}}';
    this.variantPrices = this.readJson('[data-ur-variant-prices]') || {};
    this.marketCountry = root.dataset.marketCountry || '';
    this.selected = {};
    this.pack = 2;
    this.hero = root.querySelector('[data-ur-hero]');
    this.form = root.querySelector('[data-ur-form]');
    this.variantInput = root.querySelector('[data-ur-variant-id]');
    this.quantityInput = root.querySelector('[data-ur-quantity]');
    this.optionMount = root.querySelector('[data-ur-options]');
    this.priceEl = root.querySelector('[data-ur-current-price]');
    this.comparePriceEl = root.querySelector('[data-ur-compare-price]');
    this.savingEl = root.querySelector('[data-ur-saving]');
    this.perUnitEl = root.querySelector('[data-ur-per-unit]');
    this.cta = root.querySelector('[data-ur-cta]');
    this.sticky = root.querySelector('[data-ur-sticky]');
    this.stickyMeta = root.querySelector('[data-ur-sticky-meta]');
    this.stickyCta = root.querySelector('[data-ur-sticky-cta]');
    this.discountPercent = Number(root.dataset.rotationDiscountPercent || 0);
  }

  init() {
    if (!this.product) return;

    this.discountPercent = 0;
    const initialVariant = this.product.variants.find((variant) => variant.available) || this.product.variants[0];
    this.product.options.forEach((name, index) => {
      this.selected[name] = this.isSizeOption(name) ? '' : initialVariant?.options?.[index] || this.optionValues(name)[0] || '';
    });

    this.renderOptions();
    this.bindPackSelectors();
    this.bindTabs();
    this.bindForm();
    this.bindSizing();
    this.bindSticky();
    this.bindMedia();
    this.bindTracking();
    this.updateState();
    this.track('landing_page_view');
  }

  readJson(selector) {
    const el = this.root.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  }

  optionValues(optionName) {
    const index = this.product.options.indexOf(optionName);
    return [...new Set(this.product.variants.map((variant) => variant.options[index]).filter(Boolean))];
  }

  renderOptions() {
    this.optionMount.innerHTML = '';
    this.product.options.forEach((name, index) => {
      const field = document.createElement('div');
      field.className = 'ur-options';
      const label = document.createElement('p');
      label.className = 'ur-field-label';
      label.textContent = this.isSizeOption(name) ? 'Choose your normal size' : name;
      field.append(label);

      const grid = document.createElement('div');
      grid.className = 'ur-option-grid';
      grid.dataset.optionIndex = String(index);
      grid.dataset.optionName = name;
      grid.setAttribute('role', 'group');
      grid.setAttribute('aria-label', label.textContent);
      this.optionValues(name).forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        if (this.isColourOption(name)) {
          button.className = 'ur-colour-option';
          const swatch = document.createElement('span');
          swatch.className = 'ur-colour-option__swatch';
          swatch.dataset.colour = value.toLowerCase();
          swatch.setAttribute('aria-hidden', 'true');
          const text = document.createElement('span');
          text.textContent = value;
          button.append(swatch, text);
        } else {
          button.textContent = value;
        }
        button.dataset.optionValue = value;
        button.setAttribute('aria-pressed', this.selected[name] === value ? 'true' : 'false');
        button.addEventListener('click', () => {
          if (button.disabled) return;
          this.selected[name] = value;
          this.clearError();
          this.track(this.isSizeOption(name) ? 'manual_size_selected' : this.isColourOption(name) ? 'colour_selected' : 'variant_option_selected', { option: name, value });
          this.updateState();
        });
        grid.append(button);
      });
      field.append(grid);
      this.optionMount.append(field);
    });
  }

  bindPackSelectors() {
    this.root.querySelectorAll('[data-pack]').forEach((button) => {
      button.addEventListener('click', () => {
        const nextPack = Number(button.dataset.pack || 1);
        this.pack = nextPack === 1 ? 1 : 2;
        this.track('pack_selected', { pack: this.pack });
        this.updateState();
      });
    });
  }

  bindTabs() {
    this.root.querySelectorAll('[data-ur-tabs]').forEach((tabs) => {
      const buttons = tabs.querySelectorAll('[data-ur-tab]');
      const panels = tabs.querySelectorAll('[data-ur-panel]');
      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          const index = button.dataset.urTab;
          buttons.forEach((item) => item.setAttribute('aria-selected', item === button ? 'true' : 'false'));
          panels.forEach((panel) => {
            panel.hidden = panel.dataset.urPanel !== index;
          });
          const label = button.textContent.trim().toLowerCase().replace(/\s+/g, '_');
          this.track(`${label}_comparison_viewed`);
        });
      });
    });
  }

  bindForm() {
    if (!this.form) return;
    this.form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const variant = this.currentVariant();
      if (!this.selectedSize()) {
        this.focusSizeSelection();
        return;
      }
      if (!variant || !variant.available) {
        this.showError("That combination isn't available. Your selection is still saved.");
        return;
      }
      this.setLoading(true);
      try {
        const properties = {
          Pack: this.packLabel(),
          Offer: this.pack === 2 ? 'BOGO FREE' : 'Single',
          'Fit note': 'One-time purchase'
        };
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: variant.id, quantity: this.pack, properties })
        });
        if (!response.ok) throw new Error('Cart add failed');
        this.track('add_to_cart', {
          variant_id: variant.id,
          product_id: this.product.id,
          quantity: this.pack,
          value: this.packPrice(variant) / 100
        });
        await this.refreshCartDrawer();
      } catch (error) {
        this.showError("We couldn't add that pack. Your selection is still saved. Try again.");
      } finally {
        this.setLoading(false);
      }
    });
  }

  async refreshCartDrawer() {
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    const sections = ['cart-drawer-section'];
    const response = await fetch(`/?sections=${sections.join(',')}`);
    const html = await response.json();
    const parser = new DOMParser();
    const replacement = parser.parseFromString(html['cart-drawer-section'], 'text/html');
    const newInner = replacement.querySelector('[data-hydration-key="cart-drawer-inner"]');
    const oldInner = document.querySelector('[data-hydration-key="cart-drawer-inner"]');
    if (newInner && oldInner) oldInner.replaceWith(newInner);
    this.updateCartCount();
    drawer?.show?.();
    drawer?.open?.();
    this.track('cart_viewed');
  }

  async updateCartCount() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    const cartIcon = document.querySelector('cart-icon');
    cartIcon?.renderCartBubble?.(cart.item_count || 0);
  }

  bindSizing() {
    this.root.querySelectorAll('[data-ur-open-size]').forEach((button) => {
      button.addEventListener('click', () => {
        document.querySelector('#ur-sizing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.track('size_guide_opened');
      });
    });
  }

  bindSticky() {
    if (!this.sticky || !this.hero) return;
    const syncDrawerState = () => {
      const drawerOpen = [...document.querySelectorAll('theme-drawer, dialog, [aria-modal="true"]')].some((el) => {
        return el.matches('[open], [aria-hidden="false"], .is-open, .open') || el.open === true;
      });
      this.sticky.toggleAttribute('data-drawer-open', drawerOpen);
    };
    const observer = new IntersectionObserver(
      ([entry]) => {
        this.sticky.hidden = entry.isIntersecting;
      },
      { threshold: 0.05 }
    );
    observer.observe(this.hero);
    const drawerObserver = new MutationObserver(syncDrawerState);
    document.querySelectorAll('theme-drawer, dialog, [aria-modal="true"]').forEach((el) => {
      drawerObserver.observe(el, { attributes: true, attributeFilter: ['open', 'class', 'aria-hidden'] });
    });
    document.addEventListener('click', () => window.setTimeout(syncDrawerState, 0));
    syncDrawerState();
    this.stickyCta?.addEventListener('click', () => {
      const variant = this.currentVariant();
      if (!this.selectedSize()) {
        this.focusSizeSelection();
        return;
      }
      if (!variant || !variant.available) {
        this.showError("That combination isn't available. Your selection is still saved.");
        return;
      }
      this.form?.requestSubmit();
    });
    this.root.querySelector('[data-ur-scroll-atc]')?.addEventListener('click', () => {
      this.root.querySelector('[data-ur-buybox]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  bindMedia() {
    const mainImage = this.root.querySelector('.ur-hero__image');
    const thumbnails = this.root.querySelectorAll('[data-ur-media-thumb]');
    thumbnails.forEach((button) => {
      button.addEventListener('click', () => {
        if (mainImage && button.dataset.full) {
          mainImage.src = button.dataset.full;
          mainImage.removeAttribute('srcset');
        }
        thumbnails.forEach((thumbnail) => thumbnail.removeAttribute('aria-current'));
        button.setAttribute('aria-current', 'true');
        this.track('hero_media_interaction');
      });
    });
  }

  bindTracking() {
    this.root.querySelectorAll('[data-ur-view]').forEach((section) => {
      const observer = new IntersectionObserver(
        ([entry], obs) => {
          if (!entry.isIntersecting) return;
          this.track(section.dataset.urView);
          obs.disconnect();
        },
        { threshold: 0.35 }
      );
      observer.observe(section);
    });
    this.root.querySelectorAll('[data-ur-track]').forEach((el) => {
      el.addEventListener('click', () => this.track(el.dataset.urTrack));
    });
    document.addEventListener('submit', (event) => {
      if (event.target instanceof Element && event.target.closest('[data-ur-cart-upsell]')) {
        this.track('cart_upsell_accepted');
      }
    });
    document.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[name="checkout"], .cart__checkout-button, a[href="/checkout"]')) {
        this.track('checkout_started');
      }
    });
  }

  updateState() {
    const variant = this.currentVariant();
    const pricingVariant = variant || this.pricingVariant();
    this.root.querySelectorAll('[data-pack]').forEach((button) => {
      const active = Number(button.dataset.pack || 1) === this.pack;
      button.classList.toggle('ur-pack-card--active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    this.root.querySelectorAll('[data-option-name]').forEach((grid) => {
      const name = grid.dataset.optionName;
      grid.querySelectorAll('[data-option-value]').forEach((button) => {
        const value = button.dataset.optionValue;
        const trial = { ...this.selected, [name]: value };
        const available = this.product.variants.some((item) => this.matchesSelected(item, trial) && item.available);
        button.disabled = !available;
        button.setAttribute('aria-pressed', this.selected[name] === value ? 'true' : 'false');
      });
    });

    if (this.variantInput) this.variantInput.value = variant?.id || '';
    if (this.quantityInput) this.quantityInput.value = String(this.pack);
    if (this.priceEl && pricingVariant) this.priceEl.textContent = this.formattedPackPrice(pricingVariant);
    this.updatePackPrices(pricingVariant);
    this.updateCta(variant, pricingVariant);
  }

  updatePackPrices(variant) {
    if (!variant) return;
    this.root.querySelectorAll('[data-ur-pack-price="1"], [data-ur-pack-price-secondary="1"]').forEach((el) => {
      el.textContent = this.formattedPackPrice(variant, 1);
    });
    this.root.querySelectorAll('[data-ur-pack-price="2"], [data-ur-pack-price-secondary="2"]').forEach((el) => {
      el.textContent = `${this.formattedPackPrice(variant, 2)} total · ${this.formattedPerUnit(variant, 2)} each`;
    });
    const full = Number(variant.price || 0) * this.pack;
    const packPrice = this.packPrice(variant, this.pack);
    const saving = Math.max(0, full - packPrice);
    if (this.comparePriceEl) {
      this.comparePriceEl.hidden = !(this.pack > 1 && saving > 0);
      this.comparePriceEl.textContent = this.formattedComparePrice(variant, this.pack);
    }
    if (this.savingEl) {
      this.savingEl.hidden = !(this.pack > 1 && saving > 0);
      this.savingEl.textContent = saving > 0 ? `You save ${this.formattedSaving(variant, this.pack)}` : '';
    }
    if (this.perUnitEl) {
      this.perUnitEl.textContent = `${this.formattedPerUnit(variant, this.pack)} per singlet`;
    }
  }

  updateCta(variant, pricingVariant) {
    const optionText = Object.values(this.selected).filter(Boolean).join(' / ');
    const hasSize = Boolean(this.selectedSize());
    const label = !hasSize ? 'Choose my size' : variant?.available ? this.ctaLabel(variant) : 'Selected option unavailable';
    if (this.cta) {
      this.cta.textContent = label;
      this.cta.disabled = hasSize && !variant?.available;
    }
    if (this.stickyMeta) {
      this.stickyMeta.textContent = `${this.packLabel()} · ${optionText || 'Select size'} · ${this.formattedPackPrice(pricingVariant || {})}`;
    }
    if (this.stickyCta) {
      this.stickyCta.textContent = !hasSize ? `2 for ${this.formattedPackPrice(pricingVariant || {})}` : variant?.available ? `Get ${this.pack}` : 'Unavailable';
      this.stickyCta.disabled = hasSize && !variant?.available;
    }
  }

  currentVariant() {
    if (!this.product.options.every((name) => this.selected[name])) return null;
    return this.product.variants.find((variant) => this.matches(variant, this.selected));
  }

  pricingVariant() {
    return (
      this.product.variants.find((variant) => this.matchesSelected(variant, this.selected) && variant.available) ||
      this.product.variants.find((variant) => variant.available) ||
      this.product.variants[0]
    );
  }

  matches(variant, selected) {
    return this.product.options.every((name, index) => variant.options[index] === selected[name]);
  }

  matchesSelected(variant, selected) {
    return this.product.options.every((name, index) => !selected[name] || variant.options[index] === selected[name]);
  }

  packPrice(variant, pack = this.pack) {
    const unitPrice = Number(variant.price || 0);
    if (pack === 1 || pack === 2) return unitPrice;
    return unitPrice * pack;
  }

  packLabel(pack = this.pack) {
    if (pack === 2) return '2 Foundation Singlets - BOGO Free';
    return '1 Foundation Singlet';
  }

  ctaLabel(variant = this.currentVariant() || this.pricingVariant() || {}) {
    return `Get ${this.pack} in ${this.selectedSize()} — ${this.formattedPackPrice(variant, this.pack)}`;
  }

  priceRecord(variant) {
    return this.variantPrices[String(variant?.id || '')] || null;
  }

  formattedPackPrice(variant, pack = this.pack) {
    const record = this.priceRecord(variant);
    if (record) return record[`pack${pack}`] || record.unit || '';
    return this.formatMoney(this.packPrice(variant, pack));
  }

  formattedComparePrice(variant, pack = this.pack) {
    const record = this.priceRecord(variant);
    if (record && pack === 2) return record.compare2 || record.unit || '';
    return this.formatMoney(Number(variant?.price || 0) * pack);
  }

  formattedSaving(variant, pack = this.pack) {
    const record = this.priceRecord(variant);
    if (record && pack === 2) return record.saving2 || record.unit || '';
    const full = Number(variant?.price || 0) * pack;
    return this.formatMoney(Math.max(0, full - this.packPrice(variant, pack)));
  }

  formattedPerUnit(variant, pack = this.pack) {
    const record = this.priceRecord(variant);
    if (record && pack === 2) return record.perUnit2 || record.unit || '';
    if (record) return record.unit || '';
    return this.formatMoney(this.packPrice(variant, pack) / pack);
  }

  formatMoney(cents) {
    const amount = (Number(cents || 0) / 100).toFixed(2);
    return this.moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(Number(cents || 0) / 100)));
  }

  isSizeOption(name) {
    return /size/i.test(name);
  }

  isColourOption(name) {
    return /colou?r/i.test(name);
  }

  selectedSize() {
    const sizeOption = this.product.options.find((name) => this.isSizeOption(name));
    return sizeOption ? this.selected[sizeOption] : '';
  }

  focusSizeSelection() {
    const sizeGrid = [...this.root.querySelectorAll('[data-option-name]')].find((grid) => this.isSizeOption(grid.dataset.optionName || ''));
    sizeGrid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => sizeGrid?.querySelector('button:not([disabled])')?.focus(), 350);
    this.showError('Choose your normal size before adding the pack.');
    this.track('size_selection_required');
  }

  setLoading(isLoading) {
    if (!this.cta) return;
    const variant = this.currentVariant();
    this.cta.disabled = isLoading || (Boolean(this.selectedSize()) && !variant?.available);
    this.cta.textContent = isLoading ? 'Adding...' : !this.selectedSize() ? 'Choose my size' : variant?.available ? this.ctaLabel(variant) : 'Selected option unavailable';
  }

  showError(message) {
    const error = this.root.querySelector('[data-ur-error]');
    if (!error) return;
    error.textContent = message;
    error.hidden = false;
  }

  clearError() {
    const error = this.root.querySelector('[data-ur-error]');
    if (!error) return;
    error.textContent = '';
    error.hidden = true;
  }

  track(name, detail = {}) {
    const payload = { event: name, under_rig: true, ...detail };
    window.dataLayer?.push(payload);
    document.dispatchEvent(new CustomEvent('under-rig:event', { detail: payload }));
  }
}

document.querySelectorAll('[data-ur-funnel]').forEach((root) => {
  new UnderRigFunnel(root).init();
});
